import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Address } from 'viem';
import { queryKeys } from '@/modules/query-keys';
import { fetchSanApi } from '@/modules/fetch-san-api';
import {
  getPerformanceChart,
  listAllIssuerPredictions,
  type UserPrediction,
} from '@/modules/predictions-api';
import type { MalformedRow } from '@/modules/malformed-row';
import { priceSeriesField } from '@/modules/san-api-price-series';
import { pnlPct } from './pnlPct';

type Market = UserPrediction['market'];

// The prediction's own row under its legacy "forecast" name; the market it
// was made on rides alongside as `market`.
type MarketForecast = {
  id: string;
  symbol: string;
  direction: 'up' | 'down';
  leverage: number;
  // null = never entered (`created` / `expired` / `cancelled` rows have no
  // open price); a consumer shows the trigger price or a dash there.
  openPrice: number | null;
  closePrice: number | null;
  openDate: string;
  closeDate: string | null;
  status: 'open' | 'closed';
  // unread-field-allow: consumed structurally — the forecast passes whole into the PnL helper's structurally-typed { performance, leverage } param
  performance: number;
  closeType: string | null;
  // unread-field-allow: consumed structurally — a MarketForecast passes whole as the order-edit modal's structurally-typed `order` prop
  takeProfitPrice: number | null;
  // unread-field-allow: consumed structurally — a MarketForecast passes whole as the order-edit modal's structurally-typed `order` prop
  stopLossPrice: number | null;
  // Non-null = limit order: the prediction entered (or will enter) when the
  // live price crosses this level. Consumers tell limit rows from market
  // rows on it alone.
  triggerPrice: number | null;
  slug: string;
  ipnsKeyId: string | null;
};

export type MarketPrediction = {
  forecast: MarketForecast;
  market: Market;
  issuer: {
    address: Address | null;
    username: string;
    avatar: string | null;
  };
  vote: 'agree' | 'disagree';
  userPnl: number;
  kolPnl: number;
  beatKol: number;
  // Epoch ms at which the SanR forecast auto-closes: the backend auto-closes
  // every signal exactly 14d after open ("by 2w rule") — v2 doesn't expose
  // the lifetime field, so it is hardcoded here. The underlying market's
  // resolvesAt can fire earlier, but the forecast keeps accruing PnL until
  // autoclose, so this is the user-meaningful deadline; consumers render the
  // time left against their own clock. null for an open date that doesn't
  // parse.
  autoCloseAt: number | null;
  closedBy: string | null;
  // The current asset price the row was priced with — on the profile hooks
  // the batched SanAPI fetch, which skips markets with a backend resolvePrice
  // (null there), so a consumer rendering a current price must expect null
  // on resolved markets.
  livePrice: number | null;
  // Order lifecycle: 'pending' = created trigger order awaiting its price
  // (still editable/cancelable), 'expired' = voting closed before it fired,
  // 'cancelled' = the user cancelled it (the DELETE soft-cancels — the
  // backend keeps the row). Only pending rows are actionable; expired and
  // cancelled are read-only history. null on entered (open/closed)
  // predictions.
  orderStatus: 'pending' | 'expired' | 'cancelled' | null;
};

// A prediction row whose wire payload failed the row-level parse. Consumers
// must keep it visible with a flagged treatment instead of dropping it (a
// user's own money-adjacent rows never silently vanish); it is excluded
// from every count/aggregate here.
export type MalformedProfileRow = {
  malformed: true;
  id: string;
};

export type ProfilePredictionRow = MarketPrediction | MalformedProfileRow;

export type MarketStats = {
  // unread-field-allow: consumed structurally — the stats object passes whole into the profile's structurally-typed stats section
  totalPnl: number;
};

export type EquityPoint = { v: number; date: string };

function getClosedBy(closeType: string | null): string | null {
  switch (closeType) {
    case null:
    case '':
      return null;
    case 'signal_take_profit_triggered':
      return 'TP Hit';
    case 'signal_stop_loss_triggered':
      return 'SL Hit';
    case 'user_closed_signal':
      return 'manually';
    case 'user_auto_closed_signal':
      return 'by 2w rule';
    default:
      return closeType;
  }
}

const FORECAST_LIFETIME_MS = 14 * 86400000;

function getAutoCloseAt(openDate: string): number | null {
  const openedAt = new Date(openDate).getTime();
  if (Number.isNaN(openedAt)) return null;
  return openedAt + FORECAST_LIFETIME_MS;
}

function mapFromV2(p: UserPrediction): MarketForecast {
  return {
    id: p.id,
    symbol: p.symbol,
    direction: p.direction,
    leverage: p.leverage,
    openPrice: p.openPrice,
    // Legacy semantics: closePrice on an OPEN forecast is the running current
    // price — a chart consumer plots it as the "now" point (a null would
    // become a 0 that reads as a plunge). A consumer showing a real close
    // gates on closeDate, never on closePrice.
    closePrice: p.closePrice ?? p.currentPrice,
    openDate: p.openedAt ?? p.createdAt,
    closeDate: p.closedAt,
    status: p.status === 'closed' ? 'closed' : 'open',
    performance: p.performance,
    closeType: p.closeType,
    takeProfitPrice: p.takeProfitPrice,
    stopLossPrice: p.stopLossPrice,
    triggerPrice: p.triggerPrice,
    slug: p.slug,
    ipnsKeyId: p.ipnsKeyId ?? null,
  };
}

type PriceSpec = { slug: string; quoteAsset: string };
// An alias SanAPI doesn't know comes back as a null field.
type PriceEnvelope = Record<
  string,
  { timeseriesData: { value: number }[] } | null
>;
function priceKey(s: PriceSpec): string {
  return `${s.slug}|${s.quoteAsset}`;
}
// Live "now" prices for markets without a backend resolvePrice — needed
// because resolvePrice only exists once the backend resolver runs, and
// prediction.currentPrice freezes at user close-time. Batched into a
// single GraphQL call of @/modules/san-api-price-series fields.
async function fetchLatestPrices(
  specs: PriceSpec[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (specs.length === 0) return out;
  const aliases = specs.map((s, i) => {
    const alias = `p${i}`;
    return {
      key: priceKey(s),
      body: priceSeriesField(alias, {
        slug: s.slug,
        quoteAsset: s.quoteAsset,
        from: 'utc_now-1h',
        to: 'utc_now',
        interval: '5m',
      }),
      alias,
    };
  });
  let data: PriceEnvelope | null = null;
  try {
    ({ data } = await fetchSanApi<PriceEnvelope | null>(
      `{${aliases.map((a) => a.body).join('\n')}}`,
    ));
  } catch {
    // Live prices are decoration: a transport failure keeps the null
    // envelope, so the map stays empty and kolPnl falls back to 0 for rows
    // without a resolvePrice.
  }
  for (const a of aliases) {
    const last = data?.[a.alias]?.timeseriesData.at(-1);
    if (last !== undefined) out.set(a.key, last.value);
  }
  return out;
}

// Fresh market price for money-critical decisions (trigger side, SL/TP
// validation): consumers compute those against this value, never against
// an hourly chart series (last candle up to ~1h behind) or a price
// snapshotted into local state. 15s interval — consumers are short-lived
// surfaces and SanAPI is internal/unlimited. null while loading and when
// the slug is missing or empty (nothing is fetched then); consumers fall
// back to their last known price.
export function useLivePrice(spec: {
  slug: string | null | undefined;
  quoteAsset: string;
}): number | null {
  const slug = spec.slug ?? '';
  const { data } = useQuery({
    queryKey: queryKeys.livePrice(slug, spec.quoteAsset),
    queryFn: async () => {
      const prices = await fetchLatestPrices([
        { slug, quoteAsset: spec.quoteAsset },
      ]);
      // null (not undefined) so react-query caches the "no price" result
      // instead of treating it as a failed fetch.
      return (
        prices.get(priceKey({ slug, quoteAsset: spec.quoteAsset })) ?? null
      );
    },
    enabled: slug !== '',
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
  return data ?? null;
}

// The one UserPrediction → MarketPrediction derivation: every surface that
// opens a prediction's detail builds its row through it, so one row's
// kolPnl/beatKol never depend on which surface opened it. `livePrice` is
// the caller's current price for the row's market — it prices the KOL leg
// only until the backend writes resolvePrice; null leaves the row unpriced
// (kolPnl 0, beatKol = userPnl), the same degrade as a failed live fetch.
export function toMarketPrediction(
  p: UserPrediction,
  livePrice: number | null,
): MarketPrediction {
  const forecast = mapFromV2(p);
  const market = p.market;

  const userAgreed = forecast.direction === market.direction;
  const vote: 'agree' | 'disagree' = userAgreed ? 'agree' : 'disagree';

  const userPnl = pnlPct(forecast);

  // The backend resolve price wins once written; until then KOL PnL prices
  // off the live feed. Don't key this on the date-derived market status:
  // past resolvesAt the status reads 'resolved' while resolvePrice is still
  // null until the resolver runs (sometimes hours+), which rendered a fake
  // "KOL perf 0%" for that window.
  const priceForKol = market.resolvePrice ?? livePrice;

  const kolMove =
    market.entryPrice > 0 && priceForKol != null ?
      (priceForKol - market.entryPrice) / market.entryPrice
    : 0;
  const kolPnl = (market.direction === 'down' ? -kolMove : kolMove) * 100;
  // Backend ships beatKOL = performance × leverage − kolPerformance (decimal
  // ratio) once the market resolves AND the prediction closes. For open
  // predictions it's null, so fall back to the live-price computation.
  const beatKol = p.beatKOL != null ? p.beatKOL * 100 : userPnl - kolPnl;

  return {
    forecast,
    market,
    issuer: {
      address: p.issuer.address ?? null,
      username: p.issuer.username,
      avatar: p.issuer.avatar,
    },
    vote,
    userPnl,
    kolPnl,
    beatKol,
    autoCloseAt: getAutoCloseAt(forecast.openDate),
    closedBy:
      forecast.status === 'closed' ? getClosedBy(forecast.closeType) : null,
    livePrice,
    orderStatus:
      p.status === 'created' ? 'pending'
      : p.status === 'expired' ? 'expired'
      : p.status === 'cancelled' ? 'cancelled'
      : null,
  };
}

function collectLiveSpecs(list: UserPrediction[]): PriceSpec[] {
  const seen = new Set<string>();
  const out: PriceSpec[] = [];
  for (const p of list) {
    // Skip only markets the backend actually resolved — a market past
    // resolvesAt with a null resolvePrice still needs the live price
    // (see priceForKol in toMarketPrediction).
    if (p.market.resolvePrice != null) continue;
    const spec: PriceSpec = {
      slug: p.slug,
      quoteAsset: p.market.quoteAsset,
    };
    const key = priceKey(spec);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(spec);
  }
  return out;
}

// Always filter by issuerId — these reads go through the public
// (authed: false) path, which is unscoped, so without the filter we'd
// see every user's predictions.
async function fetchMarketForecasts(
  status: 'open' | 'closed' | readonly ('created' | 'expired' | 'cancelled')[],
  issuerId: string,
): Promise<ProfilePredictionRow[]> {
  const list = await listAllIssuerPredictions({
    issuerId,
    status,
    // Ordered by the user's own action: closed history by when each
    // prediction was closed, open list by when it was opened, orders
    // (pending + expired/cancelled) by when they were placed.
    sort:
      status === 'closed' ? 'closedAt:desc'
      : Array.isArray(status) ? 'createdAt:desc'
      : 'openedAt:desc',
  });
  const real: UserPrediction[] = [];
  const malformed: MalformedRow[] = [];
  for (const row of list) {
    if (row.status === 'malformed') malformed.push(row);
    else real.push(row);
  }
  const livePrices = await fetchLatestPrices(collectLiveSpecs(real));
  return [
    ...real.map((p) =>
      toMarketPrediction(
        p,
        livePrices.get(
          priceKey({ slug: p.slug, quoteAsset: p.market.quoteAsset }),
        ) ?? null,
      ),
    ),
    // Flagged rows go last — there's no user-action date to sort them by.
    ...malformed.map((m): MalformedProfileRow => ({
      malformed: true,
      id: m.id,
    })),
  ];
}

// Aggregates exclude flagged (malformed) rows — they carry no numbers.
function computeStats(rows: ProfilePredictionRow[]): MarketStats {
  const totalPnl = rows
    .filter((p): p is MarketPrediction => !('malformed' in p))
    .reduce((sum, p) => sum + p.userPnl, 0);
  return { totalPnl: Math.round(totalPnl * 100) / 100 };
}

export function useActiveMarketForecasts(
  issuerId: string | undefined,
): UseQueryResult<ProfilePredictionRow[]> {
  const id = issuerId ?? '';
  return useQuery({
    queryKey: queryKeys.marketForecastsProfile(id, 'active'),
    queryFn: (): Promise<ProfilePredictionRow[]> =>
      fetchMarketForecasts('open', id),
    enabled: id !== '',
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useClosedMarketForecasts(
  issuerId: string | undefined,
): UseQueryResult<{ predictions: ProfilePredictionRow[]; stats: MarketStats }> {
  const id = issuerId ?? '';
  return useQuery({
    queryKey: queryKeys.marketForecastsProfile(id, 'closed'),
    queryFn: async (): Promise<{
      predictions: ProfilePredictionRow[];
      stats: MarketStats;
    }> => {
      const predictions = await fetchMarketForecasts('closed', id);
      return {
        predictions,
        stats: computeStats(predictions),
      };
    },
    enabled: id !== '',
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

// Pending trigger orders (`created`) plus the no-longer-actionable order
// history — `expired` (voting closed before the trigger fired) and
// `cancelled` (user-cancelled; the backend keeps the row) — in one list, so
// a consumer can show where every unfired order went.
export function useOrderMarketForecasts(
  issuerId: string | undefined,
): UseQueryResult<ProfilePredictionRow[]> {
  const id = issuerId ?? '';
  return useQuery({
    queryKey: queryKeys.marketForecastsProfile(id, 'orders'),
    queryFn: (): Promise<ProfilePredictionRow[]> =>
      fetchMarketForecasts(['created', 'expired', 'cancelled'], id),
    enabled: id !== '',
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function usePerformanceChart(
  issuerId: string | undefined,
): UseQueryResult<EquityPoint[]> {
  const id = issuerId ?? '';
  return useQuery({
    queryKey: queryKeys.performanceChart(id),
    queryFn: ({ signal }): Promise<EquityPoint[]> =>
      getPerformanceChart(id, signal),
    enabled: id !== '',
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
